import { useState, useEffect, useCallback, useRef } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';

export const useMCPTasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Call MCP server
  const callMCPServer = async (command, params = []) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated to use MCP commands');
    }
    
    try {
      const response = await fetch(API_ENDPOINTS.MCP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          command: `${command} ${params.join(' ')}`.trim(),
          userId: user.uid
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`MCP Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('MCP Server error:', error);
      throw error;
    }
  };

  // Load tasks from MCP server
  const loadTasks = useCallback(async () => {
    if (!user) {
      return;
    }
    
    // Prevent multiple simultaneous calls
    if (loading) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await callMCPServer('list_tasks');
      if (result.status === 'success') {
        setTasks(result.tasks || []);
        setUpdateTrigger(prev => prev + 1); // Force re-render
      } else {
        throw new Error(result.message || 'Failed to load tasks');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, loading]);

  // Add task via MCP server
  const addTask = async (taskData) => {
    if (!user) return null;
    
    try {
      const taskParams = [
        taskData.title || '',
        taskData.description || '',
        taskData.category || 'general',
        taskData.priority || 'medium',
        taskData.dueDate || '',
        taskData.completed ? 'true' : 'false'
      ];
      
      const result = await callMCPServer('add_task', taskParams);
      if (result.status === 'success') {
        // Reload tasks to get the updated list
        await loadTasks();
        return result.task;
      } else {
        throw new Error(result.message || 'Failed to add task');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error adding task:', err);
      throw err;
    }
  };

  // Update task via MCP server
  const updateTask = async (id, updates) => {
    if (!user) return null;
    
    try {
      // Find the task in local state, but don't fail if not found
      const task = tasks.find(t => t.id === id);
      if (!task) {
        console.warn('Task not found in local state, but proceeding with update anyway');
        // Create a minimal task object for the update
        const minimalTask = { id, ...updates };
        const result = await callMCPServer('update_task', [id, JSON.stringify(minimalTask)]);
        
        if (result.status === 'success') {
          // Reload tasks to ensure consistency
          await loadTasks();
          return minimalTask;
        } else {
          console.error('Update failed:', result);
          // If task not found, reload tasks to sync with database
          if (result.message && result.message.includes('not found')) {
            await loadTasks();
            return null;
          }
          throw new Error(result.message || 'Failed to update task');
        }
      }
      
      const updatedTask = { ...task, ...updates };
      
      // Update the task in the local state immediately for better UX
      setTasks(prevTasks => 
        prevTasks.map(t => t.id === id ? updatedTask : t)
      );
      
      // Use the update_task MCP command
      try {
        const result = await callMCPServer('update_task', [id, JSON.stringify(updatedTask)]);
        
        if (result.status === 'success') {
          return updatedTask;
        } else {
          // If task not found in database, reload tasks to sync
          if (result.message && result.message.includes('not found')) {
            await loadTasks();
            return null;
          }
          throw new Error(result.message || 'Failed to update task');
        }
      } catch (mcpError) {
        // If MCP update fails, revert the local state
        setTasks(prevTasks => 
          prevTasks.map(t => t.id === id ? task : t)
        );
        throw mcpError;
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating task:', err);
      throw err;
    }
  };

  // Delete task via MCP server
  const deleteTask = async (id) => {
    if (!user) {
      return;
    }
    
    try {
      const result = await callMCPServer('delete_task', [id]);
      if (result.status === 'success') {
        // Task successfully deleted from server
        // Reload tasks to ensure consistency with Firebase
        await loadTasks();
      } else {
        console.error('Delete failed with result:', result);
        throw new Error(result.message || 'Failed to delete task');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting task:', err);
      throw err;
    }
  };

  // Load tasks when user changes
  useEffect(() => {
    if (user) {
      // Clear any stale data first
      setTasks([]);
      setError(null);
      // Then load fresh data
      loadTasks();
    } else {
      setTasks([]);
    }
  }, [user?.uid]); // Only depend on user.uid, not the entire loadTasks function

  // Force refresh function to clear cache and reload
  const forceRefresh = useCallback(() => {
    setTasks([]);
    setError(null);
    if (user) {
      loadTasks();
    }
  }, [user, loadTasks]);

  return {
    tasks,
    loading,
    error,
    updateTrigger,
    addTask,
    updateTask,
    deleteTask,
    loadTasks,
    forceRefresh,
    setError
  };
};
